import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'customers.customer.read',
  category: 'customers',
  title: 'Rechercher et consulter un client',
  intent:
    "Retrouver rapidement un client existant dans votre base — par son nom, son prénom, son téléphone ou son adresse e-mail — et lire sa fiche complète pour récupérer son historique, préparer un rendez-vous ou l'ajouter à une action.",
  context: {
    why: "Rechercher un client existant avant toute action est le geste fondamental qui vous évite de créer des doublons dans votre base. Un doublon n'est pas juste un désagrément esthétique : il fragmente l'historique d'un même client entre deux fiches distinctes, ce qui vous fait perdre la vision continue de sa relation avec vous. Le client se retrouve avec deux fiches, chacune ne contenant qu'une partie de son historique, ce qui rend impossible toute analyse pertinente et complique la personnalisation des communications. La recherche est particulièrement importante avant de créer un rendez-vous, une campagne ciblée ou n'importe quelle action qui doit être rattachée à un client existant. Pulse, notamment, ne doit jamais créer un rendez-vous sans avoir d'abord vérifié que le client existe déjà — sans cette précaution, le risque de doublon est réel et grandit à mesure que votre base s'étoffe. La fiche client ouverte vous donne aussi accès à toutes les informations utiles pour bien préparer un rendez-vous : l'historique des visites, les prestations passées, les notes de vos praticiens, les tags de segmentation, les coordonnées à jour.",
    use_cases: [
      'Un client vous appelle et vous devez retrouver rapidement sa fiche : recherche par téléphone ou par nom.',
      "Vous préparez un rendez-vous à venir et vous voulez consulter l'historique du client (dernières prestations, notes) : ouvrez sa fiche complète.",
      'Vous devez vérifier si un prospect qui prend contact est déjà dans votre base pour éviter un doublon.',
      "Vous cherchez un client dont vous ne vous souvenez que du prénom ou d'une info partielle : la recherche multi-critères (nom, prénom, téléphone, email) vous aide.",
      "Vous voulez consulter les rendez-vous à venir d'un client précis : ouvrez sa fiche puis l'onglet Rendez-vous.",
    ],
  },
  required_scopes: ['customer.read'],
  ui_entry_points: [
    { route: '/customers', element: 'Barre de recherche de la liste clients' },
    { route: '/customers', element: 'Clic sur une ligne client pour ouvrir sa fiche' },
  ],
  api: {
    primary_operation_id: 'ProCustomerAppController_getProCustomer',
    related_operation_ids: [
      'ProCustomerAppController_getProCustomerById',
      'ProCustomerAppController_getProCustomerAppointments',
    ],
  },
  steps: [
    'Saisir le nom, prénom, téléphone ou email du client recherché.',
    'Repérer le bon client dans les résultats (vérifier les homonymes).',
    'Ouvrir sa fiche pour récupérer son identifiant ou consulter son historique.',
  ],
  gotchas: [
    "Étape de lecture *obligatoire avant de créer un RDV au nom d'un client existant* : il faut son identifiant. Sans recherche préalable, Pulse risque de créer un doublon de client.",
    "En cas d'homonymes, ne pas deviner : demander au pro de préciser (téléphone / email) plutôt que de choisir au hasard.",
    "Si la recherche ne renvoie personne, le client n'existe pas encore — proposer de le créer (`customers.customer.create`) avant de poser le RDV.",
  ],
  related_features: ['customers.customer.create', 'agenda.appointment.create'],
  demo_script: {
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Avant toute action, on retrouve un client existant : c’est le geste qui vous évite les doublons.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/customers'),
        caption: 'Menu latéral, Clients : votre base au complet.',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.sidebar_search,
        caption: 'La recherche est là pour retrouver n’importe quelle fiche.',
      },
      { action: 'click', target: TEST_IDS.sidebar_search, caption: 'On ouvre la recherche.' },
      { action: 'wait_for', target: TEST_IDS.search_input, caption: 'Le champ apparaît.' },
      {
        action: 'fill',
        target: TEST_IDS.search_input,
        value: 'Marie',
        caption: 'Un nom, un prénom, un téléphone ou un e-mail suffit : la fiche remonte, avec tout son historique.',
        capture: true,
      },
    ],
  },
});
